/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useState } from "react";
import { Temporal } from "@js-temporal/polyfill";

import { cssFns } from "@superweb/css";
import { Flag, useFlag } from "@superweb/flags";
import { useLocale } from "@superweb/intl";
import {
  Button,
  createComboBoxState,
  createDateFieldState,
  createPhoneFieldState,
  createTextFieldState,
  ExperimentalFormGroup,
  ExperimentalFormRow,
  PhoneField,
  TextField,
  useDebouncedState,
  useTypo,
  useUiColors,
  type ComboBoxOption,
  type ComboBoxState,
  type PhoneFieldState,
  type TextFieldState,
} from "@superweb/ui";

import { useAction, useQuery } from "@fleet/api";
import { useOnDemandV1StationsGet } from "@fleet/api/fleet-forms";

import { useLogAction } from "#internal/analytics";
import { Message, useMessage } from "#internal/intl";
import {
  AddressSuggest,
  AppLayout,
  Form,
  FormSuggest,
  NotFound,
  Offer,
  OfferDeprecated,
  PhoneVerificator,
} from "#internal/ui";
import {
  useErrorMessages,
  useOnChangePartial,
  useOnShowErrors,
  useSubmitErrorMessages,
  useValidate,
} from "#internal/utils";
import {
  searchParamTypes as t,
  useMakeLink,
  useNavigate,
  useSearchParams,
} from "#internal/router-new";
import SerbiaOfferPng from "#internal/images/serbia-offer.png";
import SerbiaOfferWebp from "#internal/images/serbia-offer.webp";
import SerbiaOfferAvif from "#internal/images/serbia-offer.avif";

import {
  DriverLicenseDetailsFields,
  type DriverLicenseDetailsState,
} from "../driver-license-details-fields";
import {
  RegionDetailsFields,
  type RegionDetailsState,
} from "../region-details-fields";
import { useGetSteps } from "../steps";
import { useTaxSystemOptions } from "../tax-system-options";
import { TaxSystemOsnoBanner } from "./tax-system-osno-banner";

const deliveryProfessionIds = new Set([
  "cargo_courier_on_foot",
  "cargo_courier_on_bicycle",
  "cargo_courier_on_scooter",
  "cargo_courier_on_moto",
  "cargo_courier_on_car",
  "cargo_courier_on_truck",
]);

type PersonalFormState = {
  phone: PhoneFieldState;
  first_name: TextFieldState;
  last_name: TextFieldState;
  address: ComboBoxState<ComboBoxOption>;
  address_apartment: TextFieldState;
  doc_address: ComboBoxState<ComboBoxOption>;
  tax_identification_number: TextFieldState;
  tax_system: ComboBoxState<ComboBoxOption>;
  email: TextFieldState;
  tax_authority_code: TextFieldState;
  station_id: ComboBoxState<ComboBoxOption>;
};

const Content = ({
  refId,
  sessionId,
  specification,
  professionId,
  employmentTypeId,
  leadId,
  previousStep,
  isMultipleProfessions,
  isMultipleEmploymentTypes,
  proId,
  isIframe,
  confirmationId,
}: {
  refId: string;
  sessionId: string;
  specification?: string;
  professionId?: string;
  employmentTypeId?:
    | "selfemployed"
    | "individual_entrepreneur"
    | "park_employee";
  leadId?: string;
  previousStep?: "profession" | "employment-type";
  isMultipleProfessions?: boolean;
  isMultipleEmploymentTypes?: boolean;
  proId?: boolean;
  isIframe: boolean;
  confirmationId?: string;
}) => {
  const message = useMessage();
  const logActionSubmit = useLogAction("submit");
  const logActionInvalid = useLogAction("invalid");
  const locale = useLocale();
  const typo = useTypo();
  const uiColors = useUiColors();
  const flag = useFlag();
  const logAction = useLogAction("contact");
  const logTaxSystemAction = useLogAction("tax_system");
  const navigate = useNavigate();
  const makeLink = useMakeLink();

  const { response: parkInfoResponse } = useQuery({
    url: "/fleet-forms/selfreg/v1/park/info",
    method: "GET",
    headers: {
      language: locale.toString(),
      "x-session-id": sessionId,
    },
    query: {
      ref_id: refId,
      employment_type: employmentTypeId,
      profession_id: professionId,
    },
  });
  const parkInfo =
    parkInfoResponse.status === 200 ? parkInfoResponse.body : undefined;

  const [formErrorMessage, setFormErrorMessage] = useState<string>();
  const errorMessages = useErrorMessages();
  const submitErrorMessages = useSubmitErrorMessages(
    parkInfo?.country_code ?? "",
  );
  const onError = (code?: string) => {
    if (code) {
      setFormErrorMessage(submitErrorMessages[code] ?? errorMessages.default);
    } else {
      setFormErrorMessage(errorMessages.default);
    }
  };

  const employmentType =
    employmentTypeId ||
    (professionId
      ? parkInfo?.profession_settings_list.find(
          (profession) => profession.profession_id === professionId,
        )?.employment_type_list?.[0]?.employment_type
      : undefined);

  const { response: suggestResponse } = useQuery({
    url: "/fleet-forms/selfreg/v1/suggest",
    method: "GET",
    headers: {
      language: locale.toString(),
    },
    query: {
      lead_id: leadId,
      ref_id: refId,
      employment_type: employmentType!,
      apply_auth: proId,
    },
  });

  const suggest =
    suggestResponse.status === 200 ? suggestResponse.body : undefined;

  const enableTestPhones = flag({
    id: "226d2703-0a1d-4796-941f-b69512e74bca",
    description: "Enable test phones for testing",
  });

  const currentProfession = professionId
    ? parkInfo?.profession_settings_list.find(
        (item) => item.profession_id === professionId,
      )
    : undefined;
  const currentEmploymentType = currentProfession?.employment_type_list?.find(
    (item) => item.employment_type === employmentTypeId,
  );

  const optionalFields =
    currentEmploymentType?.optional_fields ?? parkInfo?.optional_fields;
  const requiredFields =
    currentEmploymentType?.required_fields ?? parkInfo?.required_fields;

  const additionalFieldsMap = {
    ...(optionalFields?.reduce<Record<string, { required: boolean }>>(
      (res, item) => ({
        ...res,
        [item]: { required: false },
      }),
      {},
    ) ?? {}),
    ...(requiredFields?.reduce<Record<string, { required: boolean }>>(
      (res, item) => ({
        ...res,
        [item]: { required: true },
      }),
      {},
    ) ?? {}),
  };

  const enableTaxSystemDescription = flag({
    id: "fb7e3871-e396-4676-a5c2-2a2de4258d45",
    description: "Enable tax system description",
  });

  const taxSystemOptions = useTaxSystemOptions();

  const [personalState, setPersonalState] = useState<PersonalFormState>(() => ({
    phone: createPhoneFieldState({
      value:
        suggest?.phone ||
        (parkInfo?.country_code ? "+" + parkInfo.country_code : ""),
      errorMessage: suggest?.phone
        ? undefined
        : parkInfo?.country_code
          ? errorMessages.invalidPhone
          : errorMessages.requiredField,
    }),
    first_name: createTextFieldState({
      value: suggest?.name?.first ?? "",
      errorMessage: suggest?.name?.first
        ? undefined
        : errorMessages.requiredField,
    }),
    last_name: createTextFieldState({
      value: suggest?.name?.last ?? "",
      errorMessage: suggest?.name?.last
        ? undefined
        : errorMessages.requiredField,
    }),
    address: createComboBoxState({
      value:
        proId && suggest?.address
          ? { key: suggest.address, label: suggest.address }
          : undefined,
      errorMessage:
        additionalFieldsMap["address"]?.required && !(proId && suggest?.address)
          ? errorMessages.requiredField
          : undefined,
    }),
    address_apartment: createTextFieldState({
      value:
        proId && suggest?.address_apartment ? suggest.address_apartment : "",
      errorMessage:
        additionalFieldsMap["address_apartment"]?.required &&
        !(proId && suggest?.address_apartment)
          ? errorMessages.requiredField
          : undefined,
    }),
    doc_address: createComboBoxState({
      value:
        proId && suggest?.doc_address
          ? { key: suggest.doc_address, label: suggest.doc_address }
          : undefined,
      errorMessage:
        additionalFieldsMap["doc_address"]?.required &&
        !(proId && suggest?.doc_address)
          ? errorMessages.requiredField
          : undefined,
    }),
    tax_identification_number: createTextFieldState({
      value:
        proId && suggest?.tax_identification_number
          ? suggest.tax_identification_number
          : "",
      errorMessage:
        additionalFieldsMap["tax_identification_number"]?.required &&
        !(proId && suggest?.tax_identification_number)
          ? errorMessages.requiredField
          : undefined,
    }),
    tax_system: createComboBoxState({
      value:
        proId && suggest?.tax_system
          ? { key: suggest.tax_system, label: suggest.tax_system }
          : undefined,
      errorMessage:
        additionalFieldsMap["tax_system"] && !(proId && suggest?.tax_system)
          ? errorMessages.requiredField
          : undefined,
    }),
    email: createTextFieldState({
      value: proId && suggest?.email ? suggest.email : "",
      errorMessage:
        additionalFieldsMap["email"]?.required && !(proId && suggest?.email)
          ? errorMessages.requiredField
          : undefined,
    }),
    tax_authority_code: createTextFieldState({
      value:
        proId && suggest?.tax_identification_number
          ? suggest.tax_identification_number
          : "",
      errorMessage:
        additionalFieldsMap["tax_authority_code"]?.required &&
        !(proId && suggest?.tax_identification_number)
          ? errorMessages.requiredField
          : undefined,
    }),
    station_id: createComboBoxState({
      errorMessage: additionalFieldsMap["station_id"]
        ? errorMessages.requiredField
        : undefined,
    }),
  }));

  const enableDriverLicenseCountrySelection = flag({
    id: "21fe53a9-0779-4dd0-a573-004cadc8c5d0",
    description: "Enable driver license country selection",
  });

  const hasPhoneChanged =
    !suggest?.phone || suggest.phone !== personalState.phone.value;

  const enablePhoneVerification =
    Boolean(parkInfo?.phone_verification) && hasPhoneChanged;

  const [licenseState, setLicenseState] = useState<DriverLicenseDetailsState>(
    () => ({
      country: createComboBoxState(
        enableDriverLicenseCountrySelection
          ? proId && suggest?.driver_license?.country
            ? {
                value: {
                  key: suggest.driver_license.country,
                  label: suggest.driver_license.country,
                },
              }
            : {
                value: undefined,
                errorMessage: errorMessages.requiredField,
              }
          : undefined,
      ),
      number: createTextFieldState(
        proId && suggest?.driver_license?.number
          ? {
              value: suggest.driver_license.number,
            }
          : {
              value: "",
              errorMessage: errorMessages.requiredField,
            },
      ),
      issue_date: createDateFieldState(
        proId && suggest?.driver_license?.issue_date
          ? {
              value: Temporal.PlainDate.from(suggest.driver_license.issue_date),
            }
          : {
              errorMessage: errorMessages.requiredField,
            },
      ),
      expiry_date: createDateFieldState(
        proId && suggest?.driver_license?.expiry_date
          ? {
              value: Temporal.PlainDate.from(
                suggest.driver_license.expiry_date,
              ),
            }
          : {
              errorMessage: errorMessages.requiredField,
            },
      ),
    }),
  );

  const onChangeLicensePartial = useOnChangePartial(setLicenseState);
  const onShowLicenseErrors = useOnShowErrors(setLicenseState);

  const [regionState, setRegionState] = useState<RegionDetailsState>(() => ({
    region: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
  }));

  const onShowRegionErrors = useOnShowErrors(setRegionState);

  const [legalPhoneConfirmationId, setLegalPhoneConfirmationId] = useState("");

  const debouncedStationState = useDebouncedState(
    personalState.station_id,
    300,
  );

  const { data: stationData } = useOnDemandV1StationsGet(
    {
      query: {
        ref_id: refId,
        ...(debouncedStationState.inputValue && {
          text_query: debouncedStationState.inputValue,
        }),
      },
      headers: {
        language: locale.toString(),
      },
    },
    {
      enabled: !!additionalFieldsMap["station_id"],
    },
  );

  const stationOptions =
    stationData?.data.stations.map((item) => ({
      key: item.id,
      label: item.taxi_station_name,
    })) ?? [];

  const professionSettings = parkInfo?.profession_settings_list.find(
    (profession) => profession.profession_id === professionId,
  );
  const getSteps = useGetSteps();
  const { stepsCount, currentStep, nextStep } = getSteps({
    formRequirements:
      professionSettings?.form_requirements ?? parkInfo?.form_requirements,
    currentStep: "details",
    employmentType,
    isMultipleProfessions,
    isMultipleEmploymentTypes,
    isLoginWithPro: proId,
  });

  const onChangePersonalPartial = useOnChangePartial(setPersonalState);
  const onShowPersonalErrors = useOnShowErrors(setPersonalState);

  const { action: actionSubmit, isFetching } = useAction({
    url: "/fleet-forms/selfreg/v1/submit",
    method: "POST",
  });

  const { validateTextField, validateComboboxField, validatePhoneField } =
    useValidate();

  const optionalFieldDescription = message({
    id: "2a9668d0-a117-4d6c-a1fd-9b5b6e391363",
    context: "Yango forms. Optional field description",
    default: "Optional",
  });

  const isAddressFieldsDisabled =
    enablePhoneVerification && !confirmationId && !legalPhoneConfirmationId;

  const [licenseTemplates, setLicenseTemplates] = useState<string[]>(
    parkInfo?.license_number_templates ?? [],
  );

  const { action: getLicenseTemplatesData } = useAction({
    url: "/fleet-forms/selfreg/v1/license/number-templates",
    method: "GET",
  });

  const hasLicenseSection = parkInfo?.form_requirements.license !== "none";
  const hasRegionSection = parkInfo?.form_requirements.region !== "none";

  const showTaxSystemOsnoBanner =
    personalState.tax_system.value?.key === "OSN" ||
    personalState.tax_system.value?.key === "OSNP";

  if (!parkInfo) {
    return <NotFound />;
  }

  const title = message({
    id: "a1ebfc65-acab-4b54-88a2-9eca3d55cca7",
    context: "Yango forms. Header title",
    default: "More details",
  });

  const searchParams = new URLSearchParams({
    ref_id: refId,
    ...(sessionId ? { sessionId } : undefined),
    ...(specification ? { specification } : undefined),
    ...(professionId ? { profession_id: professionId } : undefined),
    ...(employmentTypeId
      ? { employment_type_id: employmentTypeId }
      : undefined),
    ...(isMultipleProfessions
      ? { is_multiple_professions: "true" }
      : undefined),
    ...(isMultipleEmploymentTypes
      ? { is_multiple_employment_types: "true" }
      : undefined),
    ...(leadId ? { lead_id: leadId } : undefined),
    ...(proId ? { pro_id: proId.toString() } : undefined),
    ...(confirmationId ? { confirmation_id: confirmationId } : undefined),
  });

  const isDelivery =
    !isMultipleProfessions &&
    deliveryProfessionIds.has(
      parkInfo.profession_settings_list[0]?.profession_id ?? "",
    );
  const offerType = isMultipleProfessions
    ? "multiple"
    : isDelivery
      ? "delivery"
      : "taxi";

  const enableNewOffer = flag({
    id: "58a1bd6b-2858-45da-9df8-cae1da2aac6c",
    description: "Enable new offer",
  });
  const enableSerbiaOfferImage =
    parkInfo.park_id === "1a1d6c5f1ad7423481a1bc8a8c5d09db";

  return (
    <AppLayout
      title={isIframe ? undefined : title}
      partnerName={parkInfo.partner_name ?? ""}
      content={
        <Form
          header={{
            title,
            ...(currentStep && {
              step: {
                current: currentStep,
                total: stepsCount,
              },
            }),
            ...(previousStep
              ? {
                  previousStep: makeLink(`/${previousStep}?${searchParams}`),
                }
              : {}),
          }}
          content={
            <>
              {hasLicenseSection && (
                <>
                  <div
                    css={{
                      color: uiColors.text,
                      ...typo({
                        level: "body2",
                        density: "normal",
                        weight: "medium",
                      }),
                      ...cssFns.padding("0", "16px"),
                    }}
                  >
                    <Message
                      id="0caddf9a-3784-409c-8ee5-02cae5cf264c"
                      context="Forms. License details section. Title"
                      default="Driver's license"
                    />
                  </div>
                  <DriverLicenseDetailsFields
                    state={licenseState}
                    onChangePartial={onChangeLicensePartial}
                    countryCode={parkInfo.country_code}
                    countryId={parkInfo.country_id}
                    licenseTemplates={licenseTemplates}
                  />
                </>
              )}
              <ExperimentalFormGroup
                title={message({
                  id: "27d4f2dc-bb98-4602-ab7f-f8e4a41f0be8",
                  context: "Yango forms. Employee type details",
                  default: "Employment type details",
                })}
              >
                <ExperimentalFormRow
                  columnsCount={enablePhoneVerification ? 2 : 1}
                >
                  <PhoneField
                    required
                    label={message({
                      id: "670f2a01-fe57-46ca-a288-47f866064eea",
                      context: "Yango forms. Phone number field",
                      default: "Phone number",
                    })}
                    state={personalState.phone}
                    onChange={(state) => {
                      setLegalPhoneConfirmationId("");
                      const errorMessage = validatePhoneField(state.value, {
                        isPhoneInvalid: enableTestPhones
                          ? state.invalid &&
                            !state.value.match(/^\+7000[0-9]{7}$/)
                          : state.invalid,
                      });
                      onChangePersonalPartial({
                        phone: {
                          ...state,
                          errorMessage,
                        },
                      });
                    }}
                    {...(employmentType === "selfemployed" &&
                    parkInfo.country_code === "RU"
                      ? {
                          description: message({
                            id: "f39a8ad2-b736-4452-b13f-7cd1b19f8e7c",
                            context: "Yango forms. Phone number field",
                            default:
                              "The contractor's phone number must be the same as the one used to register in My Taxes",
                          }),
                        }
                      : {})}
                  />
                  {enablePhoneVerification && (
                    <PhoneVerificator
                      phone={
                        !personalState.phone.invalid &&
                        !personalState.phone.errorMessage
                          ? personalState.phone.value
                          : ""
                      }
                      state={legalPhoneConfirmationId}
                      onChange={setLegalPhoneConfirmationId}
                    />
                  )}
                </ExperimentalFormRow>
              </ExperimentalFormGroup>
              {additionalFieldsMap["address"] && (
                <ExperimentalFormRow columnsCount={2}>
                  <AddressSuggest
                    refId={refId}
                    phone={
                      hasPhoneChanged
                        ? personalState.phone.value
                        : suggest.phone ?? ""
                    }
                    disabled={isAddressFieldsDisabled}
                    required={additionalFieldsMap["address"].required}
                    label={message({
                      id: "223b35da-d38a-439b-b278-9a3de0875d57",
                      context: "Yango forms. Actual adress field",
                      default: "Current address",
                    })}
                    description={
                      additionalFieldsMap["address"].required
                        ? undefined
                        : optionalFieldDescription
                    }
                    confirmationId={
                      hasPhoneChanged
                        ? legalPhoneConfirmationId
                        : confirmationId ?? ""
                    }
                    enablePhoneVerification={
                      enablePhoneVerification || Boolean(confirmationId)
                    }
                    state={personalState.address}
                    onChange={(state) => {
                      const errorMessage = additionalFieldsMap["address"]
                        ?.required
                        ? validateComboboxField(state)
                        : undefined;

                      onChangePersonalPartial({
                        address: {
                          ...state,
                          errorMessage,
                          errorVisible: Boolean(errorMessage),
                        },
                      });
                    }}
                  />
                  <TextField
                    required={
                      additionalFieldsMap["address_apartment"]?.required
                    }
                    state={personalState.address_apartment}
                    disabled={isAddressFieldsDisabled}
                    label={message({
                      id: "a90a2ba7-e34a-4fb5-bb05-58f28f172a77",
                      context: "Yango forms. Actual adress apartment field",
                      default: "Apartment",
                    })}
                    description={
                      additionalFieldsMap["address_apartment"]?.required
                        ? undefined
                        : optionalFieldDescription
                    }
                    onChange={(state) => {
                      onChangePersonalPartial({
                        address_apartment: {
                          ...state,
                        },
                      });
                    }}
                  />
                </ExperimentalFormRow>
              )}
              {additionalFieldsMap["doc_address"] && (
                <ExperimentalFormRow columnsCount={2}>
                  <AddressSuggest
                    refId={refId}
                    phone={
                      hasPhoneChanged
                        ? personalState.phone.value
                        : suggest.phone ?? ""
                    }
                    confirmationId={
                      hasPhoneChanged
                        ? legalPhoneConfirmationId
                        : confirmationId ?? ""
                    }
                    enablePhoneVerification={
                      enablePhoneVerification || Boolean(confirmationId)
                    }
                    disabled={isAddressFieldsDisabled}
                    required={additionalFieldsMap["doc_address"].required}
                    label={message({
                      id: "27e4dc12-a6fc-4f91-8f90-c270efabe393",
                      context: "Yango forms. Registration adress field",
                      default: "Registration address",
                    })}
                    description={
                      additionalFieldsMap["doc_address"].required
                        ? undefined
                        : optionalFieldDescription
                    }
                    state={personalState.doc_address}
                    onChange={(state) => {
                      const errorMessage = additionalFieldsMap["doc_address"]
                        ?.required
                        ? validateComboboxField(state)
                        : undefined;

                      onChangePersonalPartial({
                        doc_address: {
                          ...state,
                          errorMessage,
                          errorVisible: Boolean(errorMessage),
                        },
                      });
                    }}
                  />
                </ExperimentalFormRow>
              )}
              {(additionalFieldsMap["tax_identification_number"] ||
                additionalFieldsMap["tax_system"]) && (
                <ExperimentalFormRow columnsCount={2}>
                  {additionalFieldsMap["tax_identification_number"] && (
                    <TextField
                      required={
                        additionalFieldsMap["tax_identification_number"]
                          .required
                      }
                      state={personalState.tax_identification_number}
                      label={
                        parkInfo.country_code === "UZ"
                          ? message({
                              id: "d88ce050-eebc-4bd5-ab01-2ec8145e5f74",
                              context: "Register form. Identification number",
                              default: "Private entity ID",
                            })
                          : parkInfo.country_code === "MD"
                            ? message({
                                id: "3016ab47-1bbb-4a08-b1bb-7bb296630ed1",
                                context:
                                  "Register form. Identification number in Moldova",
                                default: "IDNP",
                              })
                            : message({
                                id: "f4663c03-450f-4394-b4e0-a37fd16d6316",
                                context: "Yango forms. Taxpayer ID field",
                                default: "Taxpayer ID",
                              })
                      }
                      description={
                        additionalFieldsMap["tax_identification_number"]
                          .required
                          ? undefined
                          : optionalFieldDescription
                      }
                      onChange={(state) => {
                        const errorMessage = additionalFieldsMap[
                          "tax_identification_number"
                        ]?.required
                          ? validateTextField(state.value)
                          : undefined;
                        onChangePersonalPartial({
                          tax_identification_number: {
                            ...state,
                            errorMessage,
                            errorVisible: !!errorMessage,
                          },
                        });
                      }}
                    />
                  )}
                  {additionalFieldsMap["tax_system"] && (
                    <FormSuggest
                      required={additionalFieldsMap["tax_system"].required}
                      data={taxSystemOptions}
                      label={message({
                        id: "7a471faa-06f3-407d-80d0-6db2ab7a6eb3",
                        context: "Yango forms. Tax system field",
                        default: "Tax system",
                      })}
                      description={
                        enableTaxSystemDescription &&
                        employmentType === "selfemployed" &&
                        parkInfo.country_code === "RU"
                          ? message({
                              id: "8e3fa3a9-e63d-482b-83f2-22cdc78aed9f",
                              context:
                                "Register form. Tax system field description",
                              default:
                                "Must match the one selected in My Taxes",
                            })
                          : additionalFieldsMap["tax_system"].required
                            ? undefined
                            : optionalFieldDescription
                      }
                      state={personalState.tax_system}
                      onChange={(state) => {
                        if (
                          personalState.tax_system.value &&
                          showTaxSystemOsnoBanner &&
                          (state.value?.key === "USN" ||
                            state.value?.key === "USNP" ||
                            state.value?.key === "AUSN")
                        ) {
                          logTaxSystemAction("tax_system_change", {
                            params: {
                              from: personalState.tax_system.value.key,
                              to: state.value.key,
                            },
                          });
                        }

                        const errorMessage = additionalFieldsMap["tax_system"]
                          ?.required
                          ? validateComboboxField(state)
                          : undefined;
                        onChangePersonalPartial({
                          tax_system: {
                            ...state,
                            errorMessage,
                            errorVisible: Boolean(errorMessage),
                          },
                        });
                      }}
                    />
                  )}
                </ExperimentalFormRow>
              )}
              <Flag
                id="2f21b011-7f2b-47f4-a214-b465a4e1ffc5"
                description="Register form. Show tax system OSNO banner"
              >
                {showTaxSystemOsnoBanner && <TaxSystemOsnoBanner />}
              </Flag>
              {(additionalFieldsMap["email"] ||
                additionalFieldsMap["tax_authority_code"] ||
                additionalFieldsMap["station_id"]) && (
                <ExperimentalFormRow columnsCount={2}>
                  {additionalFieldsMap["email"] && (
                    <TextField
                      required={additionalFieldsMap["email"].required}
                      state={personalState.email}
                      label={message({
                        id: "209bad37-f4dd-4858-b710-61ac226c3c39",
                        context: "Yango forms. Email field",
                        default: "Email",
                      })}
                      description={
                        additionalFieldsMap["email"].required
                          ? undefined
                          : optionalFieldDescription
                      }
                      onChange={(state) => {
                        const errorMessage = additionalFieldsMap["email"]
                          ?.required
                          ? validateTextField(state.value)
                          : undefined;
                        onChangePersonalPartial({
                          email: {
                            ...state,
                            errorMessage,
                            errorVisible: !!errorMessage,
                          },
                        });
                      }}
                    />
                  )}
                  {additionalFieldsMap["tax_authority_code"] && (
                    <TextField
                      required={
                        additionalFieldsMap["tax_authority_code"].required
                      }
                      state={personalState.tax_authority_code}
                      label={message({
                        id: "24ff1303-639b-4e32-a125-24f9a4ed6231",
                        context: "Yango forms. Tax authority code field",
                        default: "Tax authority code",
                      })}
                      description={
                        additionalFieldsMap["tax_authority_code"].required
                          ? undefined
                          : optionalFieldDescription
                      }
                      onChange={(state) => {
                        const errorMessage = additionalFieldsMap[
                          "tax_authority_code"
                        ]?.required
                          ? validateTextField(state.value)
                          : undefined;
                        onChangePersonalPartial({
                          tax_authority_code: {
                            ...state,
                            errorMessage,
                            errorVisible: !!errorMessage,
                          },
                        });
                      }}
                    />
                  )}
                  {additionalFieldsMap["station_id"] && (
                    <FormSuggest
                      required={additionalFieldsMap["station_id"].required}
                      data={stationOptions}
                      label={message({
                        id: "67930fc9-3693-4c09-9e2d-335d01adf9b8",
                        context: "Yango forms. Station field",
                        default: "Taxi station",
                      })}
                      description={
                        additionalFieldsMap["station_id"].required
                          ? undefined
                          : optionalFieldDescription
                      }
                      state={personalState.station_id}
                      onChange={(state) => {
                        const errorMessage = additionalFieldsMap["station_id"]
                          ?.required
                          ? validateComboboxField(state)
                          : undefined;
                        onChangePersonalPartial({
                          station_id: {
                            ...state,
                            errorMessage,
                            errorVisible: Boolean(errorMessage),
                          },
                        });
                      }}
                    />
                  )}
                </ExperimentalFormRow>
              )}
              {hasRegionSection && (
                <RegionDetailsFields
                  state={regionState}
                  onChange={setRegionState}
                  refId={refId}
                />
              )}
            </>
          }
          onSubmit={() => {
            if (hasRegionSection) {
              onShowRegionErrors();
            }

            const hasRegionErrors =
              hasRegionSection &&
              Object.keys(regionState).some(
                (field) =>
                  regionState[field as keyof RegionDetailsState]
                    .errorMessage !== undefined,
              );

            if (hasLicenseSection) {
              onShowLicenseErrors();
            }

            const hasLicenseErrors =
              hasLicenseSection &&
              Object.keys(licenseState).some(
                (field) =>
                  licenseState[field as keyof DriverLicenseDetailsState]
                    .errorMessage !== undefined,
              );

            if (hasLicenseErrors) {
              if (
                (licenseTemplates.length > 0 &&
                  licenseState.number.errorMessage ===
                    errorMessages.invalidDriverLicenseWithTemplates(
                      licenseTemplates.join(", "),
                    )) ||
                licenseState.number.errorMessage ===
                  errorMessages.invalidDriverLicense
              ) {
                logActionInvalid("invalid_driver_license", {
                  onlyYM: true,
                });
              }

              if (
                licenseState.expiry_date.errorMessage ===
                errorMessages.invalidDate
              ) {
                logActionInvalid("invalid_expiry_date", {
                  onlyYM: true,
                });
              }

              if (
                licenseState.issue_date.errorMessage ===
                errorMessages.invalidDate
              ) {
                logActionInvalid("invalid_issue_date", {
                  onlyYM: true,
                });
              }
            }

            onShowPersonalErrors();

            const hasErrors = Object.keys(personalState).some(
              (field) =>
                personalState[field as keyof PersonalFormState].errorMessage !==
                undefined,
            );

            if (hasErrors) {
              if (
                personalState.phone.errorMessage === errorMessages.invalidPhone
              ) {
                logActionInvalid("invalid_phone_number", {
                  onlyYM: true,
                });
              }
            }

            if (hasErrors || hasLicenseErrors || hasRegionErrors) {
              return;
            }

            const personal = {
              first_name: personalState.first_name.value,
              last_name: personalState.last_name.value,
              phone: suggest?.phone!,
              confirmation_id: sendValueOrUndefined(confirmationId ?? ""),
              ...(hasPhoneChanged && {
                legal_phone: personalState.phone.value,
                legal_phone_confirmation_id: sendValueOrUndefined(
                  legalPhoneConfirmationId,
                ),
              }),
              ref_id: refId,
              agree_processing_data: true,
              agree_receive_info: true,
              address: personalState.address.value?.key,
              address_apartment: sendValueOrUndefined(
                personalState.address_apartment.value,
              ),
              doc_address: personalState.doc_address.value?.key,
              tax_identification_number: sendValueOrUndefined(
                personalState.tax_identification_number.value,
              ),
              tax_system: personalState.tax_system.value?.key,
              tax_authority_code: sendValueOrUndefined(
                personalState.tax_authority_code.value,
              ),
              email: sendValueOrUndefined(personalState.email.value),
              station_id: personalState.station_id.value?.key,
              profession_id: professionId,
              employment_type: employmentTypeId,
            } as const;

            const license =
              licenseState.issue_date.value && licenseState.expiry_date.value
                ? {
                    number: licenseState.number.value,
                    issue_date: licenseState.issue_date.value.toString(),
                    expiry_date: licenseState.expiry_date.value.toString(),
                    country_id:
                      licenseState.country.value?.key ?? parkInfo.country_id,
                  }
                : undefined;

            const park = regionState.region.value
              ? {
                  region_id: regionState.region.value.key,
                }
              : undefined;

            actionSubmit(
              {
                headers: {
                  "x-submission-id": sessionId,
                  language: locale.toString(),
                  "content-type": "application/json",
                },
                query: {
                  ref_id: refId,
                  apply_auth: proId,
                },
                body: {
                  personal,
                  ...(hasLicenseSection && {
                    license,
                  }),
                  ...(hasRegionSection && {
                    park,
                  }),
                },
              },
              {
                onResponse: (res) => {
                  if (res.status === 204) {
                    logActionSubmit("submit_form", {
                      params: "no_auth",
                    });

                    navigate(`/${nextStep ?? "download-app"}?${searchParams}`);
                    return;
                  }

                  switch (res.body.code) {
                    case "invalid_phone_number": {
                      logActionInvalid("invalid_phone_number", {
                        onlyYM: true,
                      });
                      onChangePersonalPartial({
                        phone: {
                          ...personalState.phone,
                          errorMessage: errorMessages.invalidPhone,
                          errorVisible: true,
                        },
                      });
                      break;
                    }
                    case "invalid_car_number": {
                      logActionInvalid("invalid_car_number", { onlyYM: true });
                      break;
                    }
                    case "invalid_driver_license": {
                      logActionInvalid("invalid_driver_license", {
                        onlyYM: true,
                      });
                      if (enableDriverLicenseCountrySelection) {
                        getLicenseTemplatesData(
                          {
                            headers: { language: locale.toString() },
                            query: {
                              country_id:
                                licenseState.country.value?.key ??
                                parkInfo.country_id,
                            },
                          },
                          {
                            onResponse: (res) => {
                              let templates: string[] = [];
                              if (res.status === 200) {
                                setLicenseTemplates(
                                  res.body.license_number_templates,
                                );
                                templates = res.body.license_number_templates;
                              }
                              onChangeLicensePartial({
                                number: {
                                  ...licenseState.number,
                                  errorMessage:
                                    templates.length > 0
                                      ? errorMessages.invalidDriverLicenseWithTemplates(
                                          templates.join(", "),
                                        )
                                      : errorMessages.invalidDriverLicense,
                                  errorVisible: true,
                                },
                              });
                            },
                          },
                        );
                      } else {
                        onChangeLicensePartial({
                          number: {
                            ...licenseState.number,
                            errorMessage:
                              licenseTemplates.length > 0
                                ? errorMessages.invalidDriverLicenseWithTemplates(
                                    licenseTemplates.join(", "),
                                  )
                                : errorMessages.invalidDriverLicense,
                            errorVisible: true,
                          },
                        });
                      }
                      break;
                    }
                    case "invalid_issue_date": {
                      logActionInvalid("invalid_issue_date", { onlyYM: true });
                      onChangeLicensePartial({
                        issue_date: {
                          ...licenseState.issue_date,
                          errorMessage: errorMessages.invalidDate,
                          errorVisible: true,
                        },
                      });
                      break;
                    }
                    case "invalid_expiry_date": {
                      logActionInvalid("invalid_expiry_date", {
                        onlyYM: true,
                      });
                      onChangeLicensePartial({
                        expiry_date: {
                          ...licenseState.expiry_date,
                          errorMessage: errorMessages.invalidDate,
                          errorVisible: true,
                        },
                      });
                      break;
                    }
                    default: {
                      onError(res.body.code);
                      break;
                    }
                  }
                  return;
                },
              },
            );
          }}
          footer={{
            main: (
              <Button
                variant="action"
                type="submit"
                shape="squircle"
                progress={isFetching}
                text={message({
                  id: "4c52fc1e-f893-4f9a-ae46-6043009fb088",
                  context: "Yango forms. Submit button text",
                  default: "Next",
                })}
                size="l"
              />
            ),
            errorMessage: formErrorMessage,
          }}
        />
      }
      offer={
        isIframe ? undefined : enableNewOffer ? (
          <Offer
            image={
              enableSerbiaOfferImage
                ? {
                    avif: SerbiaOfferAvif,
                    png: SerbiaOfferPng,
                    webp: SerbiaOfferWebp,
                    height: "130px",
                    width: "178px",
                    padding: "24px",
                  }
                : undefined
            }
            offerText={parkInfo.promo_text}
            type={offerType}
            onPhonePress={() =>
              logAction("phone_click_v2", {
                params: { stage: "register" },
              })
            }
            onMessengerPress={() =>
              logAction("messenger_click_v2", {
                params: { stage: "register" },
              })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        ) : (
          <OfferDeprecated
            parkId={parkInfo.park_id}
            offerText={parkInfo.promo_text}
            isDelivery={specification === "delivery"}
            onPhonePress={() =>
              logAction("phone_click_v2", { params: { stage: "details" } })
            }
            onMessengerPress={() =>
              logAction("messenger_click_v2", { params: { stage: "details" } })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        )
      }
    />
  );
};

export const DetailsForm = ({ isIframe }: { isIframe: boolean }) => {
  const [
    {
      ref_id: refId,
      sessionId,
      specification,
      profession_id: professionId,
      employment_type_id: employmentTypeId,
      lead_id: leadId,
      previous_step: previousStep,
      is_multiple_professions: isMultipleProfessions,
      is_multiple_employment_types: isMultipleEmploymentTypes,
      pro_id: proId,
      confirmation_id: confirmationId,
    },
  ] = useSearchParams({
    ref_id: t.string,
    sessionId: t.string,
    specification: t.string,
    profession_id: t.string,
    lead_id: t.string,
    employment_type_id: t.enumString([
      "selfemployed",
      "individual_entrepreneur",
      "park_employee",
    ] as const),
    previous_step: t.enumString(["profession", "employment-type"] as const),
    is_multiple_professions: t.boolean,
    is_multiple_employment_types: t.boolean,
    pro_id: t.boolean,
    confirmation_id: t.string,
  });

  if (!refId || !sessionId) {
    return <NotFound />;
  }

  return (
    <Content
      refId={refId}
      leadId={leadId}
      sessionId={sessionId}
      specification={specification}
      professionId={professionId}
      employmentTypeId={employmentTypeId}
      previousStep={previousStep}
      isMultipleProfessions={isMultipleProfessions}
      isMultipleEmploymentTypes={isMultipleEmploymentTypes}
      proId={proId}
      isIframe={isIframe}
      confirmationId={confirmationId}
    />
  );
};

const sendValueOrUndefined = (value: string) => {
  return value.length > 0 ? value : undefined;
};
